import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import ReactGA from "react-ga4";
import { Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import CrawlingConfig from "./config/CrawlingConfig";
import OpenGraphSeoConfig from "./config/OpenGraphSeoConfig";
import StructuredDataSeoConfig from "./config/StructuredDataSeoConfig";
import TwitterSeoConfig from "./config/TwitterSeoConfig";
import { useAuthActions, useAuthUser } from "./core_stores/useAuthStore";
import { useIsCompactViewActions } from "./core_stores/useIsCompactViewStore";
import { useProjectsListActions } from "./core_stores/useProjectsListStore";
import { useFirebaseAuthErrorHandler } from "./hooks/useFirebaseAuthErrorHandler";
import { AuthModalProvider } from "./providers/AuthModalProvid";
import { ConsentProvider } from "./providers/ConsentProvider";
import { FreeTokensModalProvider } from "./providers/FreeTokensModalProvider";
import { DesignLibraryModalProvider } from "./providers/DesignLibraryModalProvider";
import { GradientBuilderModalProvider } from "./providers/GradientBuilderModalProvider";
import { PromptLibraryModalProvider } from "./providers/PromptLibraryModalProvider";
import { SubscriptionsModalProvider } from "./providers/SubscriptionsModalProvider";
import { CookieConsentBanner } from "./ui/common_components/CookieConsentBanner";
import { typewriterWorkerService } from "./services/typewriterWorkerService";
import { NoInternetConnectionWrapper } from "./ui/no_internet_connection/NoInternetConnectionWrapper";
import { ProjectPage } from "./ui/project/ProjectPage";
import ProjectPageGuard from "./ui/project/ProjectPageGuard";
import SideMenu from "./ui/sideMenu/SideMenu";
import SubscriptionsFailurePage from "./ui/subscriptions/SubscriptionsFailurePage";
import SubscriptionsSuccessPage from "./ui/subscriptions/SubscriptionsSuccessPage";
import { WelcomePage } from "./ui/welcome/WelcomePage";
import { BlogListPage } from "./ui/blog/BlogListPage";
import { BlogPostPage } from "./ui/blog/BlogPostPage";
import { FaqPage } from "./ui/legal/FaqPage";
import { PrivacyPolicyPage } from "./ui/legal/PrivacyPolicyPage";
import { TermsOfServicePage } from "./ui/legal/TermsOfServicePage";
import { NotFoundPage } from "./ui/not_found/NotFoundPage";
import { AppStoreRankingsPage } from "./ui/app_store_rankings/AppStoreRankingsPage";
import { AppDetailModalProvider } from "./providers/AppDetailModalProvider";
export default function App() {
  const authUser = useAuthUser();
  const { initialize: initializeAuth, dispose: disposeAuth } = useAuthActions();
  const { initialize: initializeProjectsList, dispose: disposeProjectsList } =
    useProjectsListActions();
  const {
    initListener: initCompactViewListener,
    disposeListener: disposeCompactViewListener,
  } = useIsCompactViewActions();

  useEffect(() => {
    // Disable browser's automatic scroll restoration globally at app startup
    // This must happen BEFORE any routing to prevent scroll restoration race conditions
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    if (!import.meta.env.DEV) {

      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        release: getAppSemver(),
        // GDPR: Disable automatic PII collection (IP addresses, etc.)
        sendDefaultPii: false,
        // Scrub any remaining PII from error events
        beforeSend(event) {
          if (event.user) {
            delete event.user.ip_address;
            delete event.user.email;
          }
          return event;
        },
        // integrations: [Sentry.captureConsoleIntegration()],
        _experiments: { enableLogs: true },
        replaysOnErrorSampleRate: 0.0,
        replaysSessionSampleRate: 0.0,
      });

      // Initialize Google Analytics
      const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
      if (GA_MEASUREMENT_ID) {
        ReactGA.initialize(GA_MEASUREMENT_ID);
      }
    }

    initializeAuth();
    initCompactViewListener();

    return () => {
      disposeCompactViewListener();
      disposeProjectsList();
      disposeAuth();
      // Cleanup typewriter worker service
      typewriterWorkerService.dispose();
    };
  }, []);

  useEffect(() => {
    if (authUser) {
      initializeProjectsList();
    }
  }, [authUser]);

  return (
    <ConsentProvider>
      <AuthModalProvider>
        <FreeTokensModalProvider>
          <SubscriptionsModalProvider>
            <PromptLibraryModalProvider>
              <DesignLibraryModalProvider>
                <GradientBuilderModalProvider>
                  <NoInternetConnectionWrapper>
                    <CrawlingConfig />
                    <OpenGraphSeoConfig />
                    <TwitterSeoConfig />
                    <StructuredDataSeoConfig />
                    <Router>
                      <RouterContent />
                      <CookieConsentBanner />
                    </Router>
                  </NoInternetConnectionWrapper>
                </GradientBuilderModalProvider>
              </DesignLibraryModalProvider>
            </PromptLibraryModalProvider>
          </SubscriptionsModalProvider>
        </FreeTokensModalProvider>
      </AuthModalProvider>
    </ConsentProvider>
  );
}

function getAppSemver(): string {
  const envReleaseAppId = import.meta.env.VITE_DARVIN_RELEASE_APP_ID ?? "undefined";
  let envReleaseVersion = import.meta.env.VITE_DARVIN_RELEASE_VERSION ?? "undefined";

  if (envReleaseVersion.startsWith("v")) {
    envReleaseVersion = envReleaseVersion.substring(1);
  }

  return `${envReleaseAppId}@${envReleaseVersion}`;
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    // Scroll immediately
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    // Also scroll after a short delay to handle any async content loading
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.key]);

  return null;
}

function RouterContent() {
  useFirebaseAuthErrorHandler();

  return (
    <AppDetailModalProvider>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<WelcomePage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/app-store-rankings" element={<AppStoreRankingsPage />} />
        <Route element={<ProjectPageGuard />}>
          <Route path="/project/:projectId" element={<ProjectPage />} />
        </Route>
        <Route
          path="/subscription_success"
          element={<SubscriptionsSuccessPage />}
        />
        <Route
          path="/subscription_failure"
          element={<SubscriptionsFailurePage />}
        />

        {/* Catch-all 404 route - must be last */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <SideMenu />
    </AppDetailModalProvider>
  );
}
